import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Link2, Copy, ExternalLink, Users, Calendar, Smartphone, Bike,
  ShieldCheck, RefreshCw, QrCode, Globe,
} from 'lucide-react';
import { adminApi } from '../../services/api';
import { rebaseToCurrentOrigin, getPublicOrigin } from '../../services/origin';

const ICON = {
  customer: Users,
  campaign: Calendar,
  operator: Smartphone,
  admin: ShieldCheck,
};

export default function AdminPreviewLinks() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    adminApi.get('/admin/preview-links')
      .then(r => setData(r.data))
      .catch(() => toast.error('Errore caricamento link'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Rewrite every URL to the host the admin is currently browsing.
  // Backend-generated URLs may carry a stale preview/build host; we
  // normalize them to `window.location.origin` so the link the admin
  // copies & shares is always live and clickable on this same domain.
  const sections = useMemo(() => (data?.sections || []).map(s => ({
    ...s,
    items: s.items.map(it => ({ ...it, url: rebaseToCurrentOrigin(it.url) })),
  })), [data]);
  const currentOrigin = getPublicOrigin();

  const copy = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Link copiato');
  };

  if (loading) return <div className="p-6 md:p-8 max-w-5xl text-[#64748B] text-sm">Caricamento…</div>;
  if (!data) return null;

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-5xl" data-testid="admin-preview-links-page">
      <div className="mb-5 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl tracking-tight font-bold text-[#1A1A1A]">Anteprima &amp; link</h1>
          <p className="text-[#999] text-xs mt-0.5">
            Tutti gli URL della tua piattaforma in chiaro. Cliccaci sopra per
            aprire ogni area come la vede il cliente, lo staff o tu stesso.
          </p>
        </div>
        <button onClick={load} data-testid="reload-preview-links"
          className="h-9 px-3 inline-flex items-center gap-1.5 rounded-xl border border-slate-200 text-xs font-medium text-[#64748B] hover:bg-slate-50">
          <RefreshCw size={13} /> Ricarica
        </button>
      </div>

      {/* Current-domain banner — confirms which host every link below
          will point to. Uses live window.location.origin. */}
      <div data-testid="current-domain-banner"
        className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50/70 px-4 py-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
          <Globe size={16} className="text-emerald-700" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] text-emerald-700/80 font-medium uppercase tracking-wide">Dominio attivo</p>
          <p className="text-sm text-[#1A1A1A] font-mono truncate" data-testid="current-domain-value">{currentOrigin.replace(/^https?:\/\//, '')}</p>
        </div>
        <button onClick={() => copy(currentOrigin)} data-testid="copy-current-domain"
          className="h-8 px-3 inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 text-xs font-medium text-emerald-700 hover:bg-emerald-100">
          <Copy size={12} /> Copia
        </button>
      </div>

      <div className="space-y-6">
        {sections.map(sec => (
          <section key={sec.title} data-testid={`section-${sec.title.toLowerCase().replace(/\s/g, '-')}`}>
            <h2 className="text-sm font-semibold text-[#1A1A1A] mb-2">{sec.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {sec.items.map(it => {
                const Icon = ICON[it.category] || Link2;
                return (
                  <div key={it.key} data-testid={`link-card-${it.key}`}
                    className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-3 mb-2">
                      <div className="w-9 h-9 rounded-xl bg-[#D44E20]/10 flex items-center justify-center flex-shrink-0">
                        <Icon size={15} className="text-[#D44E20]" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-[#1A1A1A] truncate">{it.label}</p>
                        {it.subtitle && (
                          <p className="text-[11px] text-[#94A3B8] mt-0.5 line-clamp-1">{it.subtitle}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="text" value={it.url} readOnly
                        data-testid={`link-url-${it.key}`}
                        className="flex-1 h-8 bg-slate-50 border border-slate-200 rounded-lg px-2.5 text-[11px] font-mono text-[#1A1A1A] focus:outline-none truncate" />
                      <button onClick={() => copy(it.url)} data-testid={`copy-${it.key}`}
                        title="Copia link"
                        className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-[#64748B] hover:bg-slate-50">
                        <Copy size={13} />
                      </button>
                      <a href={it.url} target="_blank" rel="noopener noreferrer" data-testid={`open-${it.key}`}
                        title="Apri in nuova scheda"
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#D44E20] text-white hover:bg-[#B83D15]">
                        <ExternalLink size={13} />
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
